import { createContext, useContext, useEffect, useRef, useState } from "react";

/** The theme scrolls inside its own container, not the window; scenes read it from here. */
export const ScrollerContext = createContext({ current: null });

const clamp = (value) => Math.min(1, Math.max(0, value));

/** Maps overall progress p onto a sub-range [start, end] -> 0..1. */
export const segment = (p, start, end) => clamp((p - start) / (end - start));

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Writes scroll progress for `ref` into CSS custom properties, without re-rendering.
 * - mode "pin":  0 when the section's top reaches the viewport top, 1 when its bottom
 *                reaches the viewport bottom. Used by tall sections with a sticky stage.
 * - mode "pass": 0 when the element enters from below, 1 when it has left at the top.
 * - mode "exit": 0 while the element's top is at (or below) the viewport top, 1 once it has
 *                scrolled fully away. Used by the opening scene.
 * `vars(p, reduced)` returns the custom properties to set; optional `onUpdate(p)` runs
 * on every frame for logic that needs the progress itself (keep it cheap).
 */
export const useScrollScene = (ref, { mode = "pass", vars = (p) => ({ "--p": p }), onUpdate } = {}) => {
  const scrollerRef = useContext(ScrollerContext);
  const varsRef = useRef(vars);
  varsRef.current = vars;
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const scroller = scrollerRef.current || window;
    const reduced = prefersReducedMotion();
    let frame = 0;

    const update = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = {
        pin: () => -rect.top / Math.max(1, rect.height - vh),
        exit: () => -rect.top / Math.max(1, rect.height),
        pass: () => (vh - rect.top) / (vh + rect.height),
      };
      const p = clamp(progress[mode]());
      Object.entries(varsRef.current(p, reduced)).forEach(([name, value]) =>
        el.style.setProperty(name, value.toFixed(4)),
      );
      if (onUpdateRef.current) onUpdateRef.current(p);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    scroller.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      scroller.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(frame);
    };
  }, [ref, scrollerRef, mode]);
};

/** True once the element has scrolled into view (or every time, with once=false). */
export const useInView = (ref, { threshold = 0.2, once = true } = {}) => {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold, once]);

  return inView;
};
